import { Dispatch } from "redux";
import { Action } from "./actions";
import { SystemActions } from "./action_types";
import { System, Configuration, ApiSystem } from "../types";
import axios from "axios";

const DEFAULT_CLIENT_PROCESS_TEMPLATE = {
      allow_cancel_vehicle: false,
      display_screen_sleep_timer: 60,
      phone_sync: false,
      phone_uc_upload_delay: false,
      actuator_leg_cycle_time: 20,
      use_lighting_controller: false,
      vin_length_err_terminology: "Wrong Identifier Length",
      s3_bucket: "collision-works-d7-dev",
      enabled: {
        0: { name: "WizardVinConfirmation", id: 5 },
        1: { name: "WizardManual", id: 4 },
        2: { name: "WizardExternal", id: 2 },
      },
      location_id: 13,
      cameras: [],
      prefix_removal: 0,
      use_nhtsa_database: false,
      dual_hand_scan_capture: false,
      preferances: {
        vinchar: false,
        vinBypass: false,
        skipExternal: false,
        skipPano: false,
      },
      auto_scan_enabled: false,
      disabled: {
        0: { name: "WizardPano", id: 1 },
        1: { name: "WizardFinal", id: 3 },
        2: { name: "WizardManualPano", id: 6 },
      },
      phone_camera_lens: "wide",
      auto_scan_countdown_length: 3,
      skip_interior_simultaneous: false,
      vin_length: 17,
      actuator_fold_trigger_distance: 300,
      auto_scan_pano: false,
      unique_id: null,
      actuator_leg_stagger_time: 1,
      overlay: null,
      phone_sync_terminology: "SCAN VIN",
      vin_feedback_terminology: "QA2 Confirmed",
      // 🔹 Default/Display flags depend on how many configs exist
      is_default: false,
      is_display_tablet_rooftop: false,
      manual_scan_countdown_length: 3,
      vin_scan_terminology: "QA2",
      url: "https://collision.3iavi.dev",
      ums_environment: "dev",
      overlays: [],
      name: '',
      phone_camera_resolution: "16:9",
      tablet_rooftop_select: false,
      phone_leg_control: false,
      actuator_trigger_count_threshold: 3,
    };

export const fetchSystems = () => {
  return async (dispatch: Dispatch<Action>) => {
    try {
      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/system`;
      const headers = { Authorization: `bwenvdev` };

      const { data } = await axios.get(url, { headers });
      const normalizedData = normalizeSystemsData(data.systems);

      dispatch({
        type: SystemActions.FETCH_SYSTEMS,
        payload: normalizedData
      });
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
};

export const createSystem = (body: System) => {
  return async (dispatch: Dispatch<Action>) => {
    try {
      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/system/create`;
      const headers = { Authorization: `bwenvdev` };

      return axios.post(url, body)
        .then(_ => {
          dispatch({
            type: SystemActions.CREATE_SYSTEM,
            payload: body
          });
        });


    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
};

export const saveOrUpdateSystem = (system: ApiSystem) => {
  return async (dispatch: Dispatch<Action>) => {
    try {
      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/system`;
      const headers = { Authorization: `bwenvdev` };

      // ✅ Dynamo expects { system: { ...data } }
      const body = { system };

      const { data } = await axios.put(url, body, { headers });

      console.log("✅ System saved/updated:", data);

      dispatch({
        type: SystemActions.UPDATE_SYSTEM,
        payload: system, // or data.system if backend returns full object
      });

      return data;
    } catch (error) {
      console.error("Error updating system:", error);
      throw error;
    }
  };
};

export const deleteSystem = (systemCode: string) => {
  return async (dispatch: Dispatch<Action>) => {
    const baseUrl = `${process.env.REACT_APP_UMS_API_BASE_URL}/system/${systemCode}`;
    const headers = { Authorization: `bwenvdev` };

    return axios.delete(baseUrl, { headers })
      .then(() => {
        dispatch({
          type: SystemActions.DELETE_SYSTEM,
          payload: systemCode
        });
      })
      .catch(err => {
        console.error("Error deleting system:", err);
      });
  };
};


export const updateSelectedSystem = (system: string) => {
  console.log(system)
  return async (dispatch: Dispatch<Action>) => {
    dispatch({
      type: SystemActions.SELECT_SYSTEM,
      payload: system
    })
  }
}

/**
 * Creates a new configuration for the given system and saves it.
 */
export const createConfig = (system: System) => {
  return async (dispatch: Dispatch<Action>): Promise<Configuration> => {
    try {
      const updatedSystem = structuredClone(system) as any;
      const existingConfigs = Object.values(updatedSystem.configurations || {});
      const newConfig = buildNewConfig(existingConfigs);

      updatedSystem.configurations = {
        ...(updatedSystem.configurations || {}),
        [newConfig.clientProcess.name]: newConfig,
      };

      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/system`;
      const headers = { Authorization: `bwenvdev` };
      const body = { system: updatedSystem };

      await axios.put(url, body, { headers });

      dispatch({
        type: SystemActions.UPDATE_SYSTEM,
        payload: updatedSystem,
      });

      return newConfig;
    } catch (error) {
      console.error("Error creating configuration:", error);
      throw error;
    }
  };
};

export const duplicateConfig = (system: System, configName: string) => {
  return async (dispatch: Dispatch<Action>) => {
    try {
      const updatedSystem = structuredClone(system) as any;
      if (!updatedSystem?.configurations?.[configName]) {
        console.error("Cannot duplicate — config not found:", configName);
        return null;
      }

      const baseConfig = structuredClone(updatedSystem.configurations[configName]);
      const existingConfigs = Object.values(updatedSystem.configurations || {});

      // 🔹 Create a copy name
      const newName = baseConfig.clientProcess.name.endsWith("_copy")
        ? `${baseConfig.clientProcess.name}_2`
        : `${baseConfig.clientProcess.name}_copy`;

      // 🔹 Make sure it's unique
      const nameExists = existingConfigs.some(
        (cfg: any) => cfg.clientProcess?.name === newName
      );
      const uniqueName = nameExists ? `${newName}_${Date.now()}` : newName;

      // 🔹 Reset flags so duplicate is never default
      baseConfig.clientProcess = {
        ...baseConfig.clientProcess,
        is_default: false,
        is_display_tablet_rooftop: false,
        name: uniqueName,
      };

      updatedSystem.configurations = {
        ...(updatedSystem.configurations || {}),
        [uniqueName]: baseConfig,
      };

      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/system`;
      const headers = { Authorization: `bwenvdev` };
      const body = { system: updatedSystem };

      await axios.put(url, body, { headers });

      dispatch({
        type: SystemActions.UPDATE_SYSTEM,
        payload: updatedSystem,
      });

      console.log("✅ Duplicated config:", uniqueName);
      return baseConfig;
    } catch (err) {
      console.error("Error duplicating config:", err);
      return null;
    }
  };
};



/**
 * Deletes a configuration from a system and persists it.
 * Ensures the config is removed from the local Redux store and backend.
 */
export const deleteConfig = (system: System, configName: string) => {
  return async (dispatch: Dispatch<Action>) => {
    try {
      // ✅ Clone system so we don’t mutate state directly
      const updatedSystem = structuredClone(system) as any;

      // ✅ Ensure the config exists
      if (!updatedSystem.configurations?.[configName]) {
        console.warn(`Config "${configName}" not found in system ${system.systemCode}`);
        return;
      }

      // ✅ Safety check – don’t allow deleting defaults
      const cp = updatedSystem.configurations[configName]?.clientProcess;
      if (cp?.is_default || cp?.is_display_tablet_rooftop) {
        alert(
          "You cannot delete a configuration that is marked as Default or Display Tablet Default."
        );
        return;
      }

      // ✅ Delete the config
      delete updatedSystem.configurations[configName];

      // ✅ Persist update to backend
      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/system`;
      const headers = { Authorization: `bwenvdev` };
      const body = { system: updatedSystem };

      const { data } = await axios.put(url, body, { headers });

      console.log("✅ Config deleted and system updated:", data);

      // ✅ Update Redux state
      dispatch({
        type: SystemActions.UPDATE_SYSTEM,
        payload: updatedSystem,
      });

      return data;
    } catch (error) {
      console.error("Error deleting configuration:", error);
      throw error;
    }
  };
};

/**
 * Generates a unique config name like "NewConfig_1", "NewConfig_2", etc.
 * Ensures it doesn't already exist in the given list of configs.
 */
export const getNextUniqueConfigName = (configs: any[]): string => {
  const existingNames = new Set(configs.map((c: any) => c.clientProcess?.name || ""));

  let index = 1;
  let newName = `NewConfig_${index}`;
  while (existingNames.has(newName)) {
    index++;
    newName = `NewConfig_${index}`;
  }
  
  return newName;
};

/**
 * Builds a new configuration with all default values.
 * If it's the only config in the system, sets it as the default + tablet display config.
 */
export const buildNewConfig = (existingConfigs: any[] = []): any => {
  const newConfigName = getNextUniqueConfigName(existingConfigs)

  const isFirstConfig = existingConfigs.length === 0;

  const newConfig = {
    clientProcess: {
      ...DEFAULT_CLIENT_PROCESS_TEMPLATE,
      is_default: isFirstConfig,
      is_display_tablet_rooftop: isFirstConfig,
      name: newConfigName
    },
    assetProcessing: {},
    assetStorage: {},
    assetSyndication: {},
  };

  return newConfig;
};

/**
 * Iterates over systems data from the API and ensures every configuration
 * has a `clientProcess.name` property, using the config's key as a fallback.
 * This normalizes legacy data formats to prevent crashes in the UI.
 * @param systems The raw array of systems from the API.
 * @returns A new array of systems with normalized configurations.
 */
const normalizeSystemsData = (systems: ApiSystem[]): ApiSystem[] => {
  // Use .map to return a new array, preserving immutability
  return systems.map(system => {
    // If there are no configurations, there's nothing to fix.
    if (!system.configurations) {
      return system;
    }

    // Create a new object for the updated configurations
    const normalizedConfigurations: { [key: string]: Configuration } = {};

    // Loop over each configuration by its key (e.g., "umstest", "NewConfig_1")
    for (const configKey in system.configurations) {
      // Standard practice to ensure we're not iterating over prototype properties
      if (Object.prototype.hasOwnProperty.call(system.configurations, configKey)) {
        const originalConfig = system.configurations[configKey];

        // ❗ THE CORE LOGIC: Check if clientProcess.name is missing.
        if (!originalConfig.clientProcess?.name) {
          // If it's missing, create a new config object that includes a
          // clientProcess object with the name set to its key.
          normalizedConfigurations[configKey] = {
            ...originalConfig,
            clientProcess: {
              ...(originalConfig.clientProcess || {...DEFAULT_CLIENT_PROCESS_TEMPLATE}), // Preserve other clientProcess fields if they exist
              name: configKey, // Inject the name!
            },
          };
        } else {
          // If the name exists, the config is fine. Just copy it over.
          normalizedConfigurations[configKey] = originalConfig;
        }
      }
    }

    // Return a new system object with the cleaned-up configurations
    return {
      ...system,
      configurations: normalizedConfigurations,
    };
  });
};
